import { api } from "@/api"
import { useUserStore } from "@/store/user"
import {
  ANONYMOUS_SURVEY_CLICK_THRESHOLD,
  deferSurveyUntilTomorrow,
  getOrCreateSurveyDeviceId,
  hasReachedAnonymousSurveyClickThreshold,
  incrementAnonymousSurveySecondMenuClick,
  isAnonymousSurveyAnswered,
  isSurveyDeferredToday,
  markAnonymousSurveyAnswered,
} from "@/utils/surveyDevice"
import { v4 as uuid } from "uuid"
import { ref } from "vue"

const scoreOptions = [5, 4, 3, 2, 1]
const isVisible = ref(false)
const surveyQuestions = ref([])
const questionAnswer = ref([])
const surveyVersion = ref("")
const surveyMode = ref("login")
const surveyAudienceKey = ref("")

export function useSurveyPopup() {
  async function showLoginSurveyIfPossible() {
    const userStore = useUserStore()
    if (userStore.isLoggedIn !== true || !userStore.accessToken) {
      return
    }

    try {
      const result = await api.survey.isPossibleSatisfactionSurvey({
        schoId: userStore.scholId,
        userId: userStore.userId,
      })
      const data = result.data?.data
      const version = data?.surveyVersion || ""
      const audienceKey = loginSurveyAudienceKey(userStore.userId)
      if (isSurveyDeferredToday(window.localStorage, version, audienceKey)) {
        return
      }
      openSurveyFromResponse(data, "login", audienceKey)
    } catch (error) {
      if (error.sessionUnauthorizedHandled) return
      console.log(error.response?.data?.message || error.message)
    }
  }

  async function trackAnonymousSecondMenuClick() {
    const userStore = useUserStore()
    if (userStore.isLoggedIn === true) {
      return
    }

    const clickCount = incrementAnonymousSurveySecondMenuClick(window.localStorage)
    if (clickCount < ANONYMOUS_SURVEY_CLICK_THRESHOLD) {
      return
    }

    await showAnonymousSurveyIfPossible()
  }

  async function showAnonymousSurveyIfClickThresholdReached() {
    const userStore = useUserStore()
    if (userStore.isLoggedIn === true) {
      return
    }

    if (!hasReachedAnonymousSurveyClickThreshold(window.localStorage)) {
      return
    }

    await showAnonymousSurveyIfPossible()
  }

  async function showAnonymousSurveyIfPossible() {
    const deviceId = getOrCreateSurveyDeviceId(window.localStorage, uuid)

    try {
      const result = await api.survey.isPossibleAnonymousSatisfactionSurvey({
        device_id: deviceId,
      })
      const data = result.data?.data
      const version = data?.surveyVersion || ""
      if (isAnonymousSurveyAnswered(window.localStorage, version)) {
        return
      }
      const audienceKey = anonymousSurveyAudienceKey(deviceId)
      if (isSurveyDeferredToday(window.localStorage, version, audienceKey)) {
        return
      }
      openSurveyFromResponse(data, "anonymous", audienceKey)
    } catch (error) {
      console.log(error.response?.data?.message || error.message)
    }
  }

  async function saveSurvey() {
    if (surveyQuestions.value.some((question, index) => isRequiredSurveyQuestion(question) && questionAnswer.value[index] === "")) {
      alert("필수 문항을 모두 작성해야 제출이 가능합니다.")
      return
    }

    const answers = surveyQuestions.value.map((question, index) => ({
      question_order: question.questionOrder,
      question_type: question.questionType,
      question: question.question,
      answer: questionAnswer.value[index],
    }))

    try {
      if (surveyMode.value === "anonymous") {
        const deviceId = getOrCreateSurveyDeviceId(window.localStorage, uuid)
        await api.survey.saveAnonymousSatisfaction({
          device_id: deviceId,
          answers,
        })
        markAnonymousSurveyAnswered(window.localStorage, surveyVersion.value)
      } else {
        await api.survey.saveSatisfaction(answers)
      }

      alert("소중한 의견 감사합니다.")
      closeSurvey()
    } catch (error) {
      if (error.sessionUnauthorizedHandled) return
      alert(error.response?.data?.message || error.message)
    }
  }

  function closeSurvey() {
    isVisible.value = false
  }

  function deferSurveyAndClose() {
    deferSurveyUntilTomorrow(window.localStorage, surveyVersion.value, surveyAudienceKey.value)
    closeSurvey()
  }

  return {
    closeSurvey,
    deferSurveyAndClose,
    isRequiredSurveyQuestion,
    isVisible,
    questionAnswer,
    saveSurvey,
    scoreOptions,
    showAnonymousSurveyIfClickThresholdReached,
    showLoginSurveyIfPossible,
    surveyQuestions,
    trackAnonymousSecondMenuClick,
  }
}

function openSurveyFromResponse(data, mode, audienceKey) {
  const questions = data?.questions || []
  if (data?.result !== true || questions.length === 0) {
    setSurveyQuestions([])
    isVisible.value = false
    return
  }

  surveyMode.value = mode
  surveyVersion.value = data.surveyVersion || ""
  surveyAudienceKey.value = audienceKey || ""
  setSurveyQuestions(questions)
  isVisible.value = true
}

function setSurveyQuestions(questions) {
  surveyQuestions.value = normalizeSurveyQuestions(questions)
  questionAnswer.value = surveyQuestions.value.map(() => "")
}

function normalizeSurveyQuestions(questions) {
  return (questions || []).map(question => ({
    questionOrder: question.question_order,
    questionCode: question.question_code,
    questionType: question.question_type,
    question: question.question,
    requiredYn: question.required_yn,
  }))
}

function isRequiredSurveyQuestion(question) {
  return question.requiredYn === "Y"
}

function loginSurveyAudienceKey(userId) {
  return userId ? `login.${userId}` : ""
}

function anonymousSurveyAudienceKey(deviceId) {
  return deviceId ? `anonymous.${deviceId}` : ""
}
